import { z } from "zod";
/**
 * Granularité positionnelle IGN (#796) — PAS `_type` (nature `address`/`poi`
 * de la feature, utilisée par la sélection « adresse-d'abord, POI-secours »
 * de #566). Deux champs distincts de la réponse IGN, à ne jamais confondre.
 * Domaine confirmé empiriquement : `housenumber`, `street`, `municipality`,
 * et `locality` (lieu-dit rural — voir la fixture réelle
 * `platform-server/src/geo/__fixtures__/locality_lieu_dit.json`, seule valeur
 * que l'investigation initiale du ticket n'avait pas pu déclencher).
 *
 * `toponym` (#571) : valeur ajoutée par nous, absente du domaine IGN. Une
 * feature `_type: "poi"` ne porte jamais le champ `type` — corrélation vérifiée
 * 17/17 sur échantillon réel (calibrage #797). Ce secours concerne ~10 % des
 * adresses (lieux-dits ruraux) : c'est un chemin nominal, pas une erreur, donc
 * il mérite mieux qu'un `null` qui le confondrait avec le vrai inconnu.
 *
 * Nommée d'après ce qu'elle mesure (un centroïde de lieu-dit nommé), PAS
 * d'après sa provenance : l'appeler `poi` aurait recopié la valeur de `_type`
 * que l'en-tête ci-dessus dit justement de ne pas confondre.
 *
 * `unknown` (#807) : l'IGN a résolu l'adresse mais n'a pas dit à quel niveau —
 * feature non-POI sans champ `type`, ou `type` hors de notre domaine. Ce n'est
 * PAS une granularité mesurée, c'est l'absence d'information, rendue explicite
 * pour qu'une colonne puisse être `NOT NULL` sans mentir. Se lit « position
 * exploitable, finesse indéterminée » : la doctrine #797 la traite comme un
 * DOUTE (on l'accepte), jamais comme la certitude négative qu'est `municipality`.
 *
 * ORDRE : les valeurs sont rangées du plus précis au plus grossier, et
 * `toponym` s'insère entre `street` et `municipality` — un lieu-dit habité est
 * positionnellement l'équivalent d'un `locality`, PAS le pire cas de l'échelle.
 * Tout tri ou gate construit sur cet enum (#797) doit respecter cet ordre.
 *
 * ⚠️ `unknown` est HORS de cette échelle et ne doit jamais être comparée par
 * rang. Elle est en dernière position pour des raisons de stockage (l'index
 * ENUM MySQL des valeurs existantes ne bouge pas), pas parce qu'elle serait la
 * plus grossière : une adresse sans `type` peut être un numéro de rue exact.
 * Un gate ordinal la lirait comme pire que `municipality` et refuserait des
 * positions parfaitement bonnes — le traiter par un cas explicite, pas par `<`.
 */
export const GEOCODE_PRECISION_VALUES = [
    "housenumber",
    "street",
    "locality",
    "toponym",
    "municipality",
    "unknown",
];
export const GeocodePrecisionSchema = z.enum(GEOCODE_PRECISION_VALUES);
/**
 * Provenance d'une position géocodée. `geo-portail` = rendue par
 * `fetchGeocode`/`apiIgn`, seule source automatique actuelle. `manual` =
 * correction humaine posée volontairement — un bouclier contre l'écrasement
 * par un géocodage automatique ultérieur (voir `docs/context/geocodage.md`
 * §1 pour la sémantique précise de cette valeur).
 *
 * Valeurs partagées entre `address.geocode_source` et
 * `customer.service_geocode_source` : une seule source de vérité pour
 * l'ENUM, pas deux tableaux dupliqués qui pourraient diverger.
 */
export const GEOCODE_SOURCE_VALUES = ["geo-portail", "manual"];
export const GeocodeSourceSchema = z.enum(GEOCODE_SOURCE_VALUES);
//# sourceMappingURL=geocode.model.js.map